import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AppShell as DSAppShell,
  Badge,
  Banner,
  BotAvatar,
  Button,
  Chat,
  Modal,
  SCLogo,
  SideNav,
  sideNavItemClassName,
  pickActivityLabel,
  StatusIndicator,
  TOOL_ACTIVITY_WRITING_LABELS,
} from '@servicecompressionllc/sc-design-system';
import { Eye, Share2, Trash2 } from '@servicecompressionllc/sc-design-system/icons';
import { PlatformHeader } from '../../components/PlatformHeader.js';
import { useMe } from '../../hooks/useMe.js';
import { formatDayTime } from '../../lib/format.js';
import { hasAdminAccess } from '../../lib/permissions.js';
import { hasAgentAccess } from '../../lib/scopes.js';
import { useChatLogic } from './useChatLogic.js';
import { useMyBotsLogic, type MyBotEntry } from './useMyBotsLogic.js';
import { ManageSharingModal } from './sharing/ManageSharingModal.js';
import { ShareDialog } from './sharing/ShareDialog.js';
import { SharedConversationView } from './sharing/SharedConversationView.js';
import { PushAskBanner } from '../push/PushAskBanner.js';
import { SharedWithMeSection } from './sharing/SharedWithMeSection.js';
import { WhatIWatchPanel } from './whatIWatch/WhatIWatchPanel.js';
import { useSharingLogic } from './sharing/useSharingLogic.js';
import type { ChatMessage, Conversation } from './types.js';
import type { BotQuestion } from '../../lib/types.js';

/** Per-rating reason chips for the assistant feedback footer (SC-domain copy). */
const FEEDBACK_CATEGORIES = {
  up: ['Accurate', 'Helpful', 'Clear'],
  down: ['Inaccurate', 'Missing info', 'Not relevant', 'Unsafe'],
};

/** A bot pinned at the top of the rail: its ring, its name, and what's new or whether it's listening. */
const BotPin = ({
  bot,
  active,
  onOpen,
}: {
  bot: MyBotEntry;
  active: boolean;
  onOpen: () => void;
}) => {
  // The open thread is being read, so its own count never shows.
  const unread = active || bot.turnedOff ? 0 : bot.unread;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-current={active ? 'page' : undefined}
      className={`flex w-full items-center gap-2.5 rounded-lg border bg-surface px-2.5 py-2 text-left transition-colors ${
        active ? 'border-primary' : 'border-outline-strong hover:border-ink-subtle'
      }`}
    >
      {/* The canvas ring: 3px, its inner disc the card's own colour. It moves only when
          there's something new, and dims when the bot is off. */}
      <BotAvatar
        size="lg"
        state={unread > 0 ? 'new' : 'idle'}
        className={`!h-8 !w-8 [&_.bg-surface-elevated]:bg-surface ${
          unread > 0 ? '' : '[&>span]:!animate-none'
        } ${bot.turnedOff ? 'opacity-45' : ''}`}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-semibold text-ink">{bot.name}</span>
        {bot.turnedOff ? (
          <span className="text-[11px] leading-4 text-ink-subtle">Off</span>
        ) : unread > 0 && bot.latestHeadline ? (
          <span className="truncate text-[11px] font-medium leading-4 text-ink">
            {bot.latestHeadline}
          </span>
        ) : (
          <StatusIndicator
            tone="success"
            pulse
            className="gap-1.5 text-[11px] leading-4 text-success"
          >
            Listening
          </StatusIndicator>
        )}
      </span>
      {unread > 0 && (
        <span
          aria-label={`${unread} unread`}
          className="inline-flex h-[18px] min-w-[18px] flex-shrink-0 items-center justify-center rounded-full bg-primary px-[5px] text-[11px] font-semibold text-white"
        >
          {unread}
        </span>
      )}
    </button>
  );
};

/**
 * The platform's primary surface: the assistant. Chat is feature #1 — the left
 * rail is its conversation list. The role-gated jump to Admin lives in the user
 * menu (PlatformHeader), not the rail. Access to the assistant itself is gated
 * on the `Agent.Run` scope; the orchestrator re-checks on every send regardless.
 */
export const ChatPage = () => {
  const me = useMe();
  const [navOpen, setNavOpen] = useState(false);
  const [watchingOpen, setWatchingOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Conversation | null>(null);
  const myBots = useMyBotsLogic(hasAgentAccess(me.data?.user.effectiveScopes));
  const {
    conversations,
    activeId,
    messages,
    draft,
    isTyping,
    streamingMessageId,
    liveMessageId,
    submitFeedback,
    feedbackByMessage,
    isLoadingFeedback,
    isLoadingMessages,
    earlier,
    setDraft,
    selectConversation,
    newConversation,
    openCaseConversation,
    send,
    answerQuestion,
    deleteConversation,
  } = useChatLogic(myBots.threadIds);
  const activeBot = myBots.botForThread(activeId);

  // Keep focus in the composer across turns. Sending flips `isTyping`, which
  // disables the <textarea> (the browser blurs a disabled element), so the
  // cursor is lost every send. When the turn finishes the input re-enables —
  // restore focus on the true→false edge so you can keep typing without
  // re-clicking. DOM-focus sync to the typing state, same shape as the scroll
  // sync above; the edge guard means it never steals focus mid-interaction.
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Older messages land above what they're reading; keep it where it was on screen.
  const showEarlier = async (): Promise<void> => {
    const list = listRef.current;
    const fromBottom = list ? list.scrollHeight - list.scrollTop : 0;
    await earlier.load();
    requestAnimationFrame(() => {
      if (list) list.scrollTop = list.scrollHeight - fromBottom;
    });
  };
  const wasTyping = useRef(false);
  useEffect(() => {
    if (wasTyping.current && !isTyping) inputRef.current?.focus();
    wasTyping.current = isTyping;
  }, [isTyping]);

  // Gate on EFFECTIVE scopes (direct ∪ group-inherited), not direct grants —
  // otherwise a user who got Agent.Run via a group is wrongly shown "no access".
  const scopes = me.data?.user.effectiveScopes;
  const canChat = hasAgentAccess(scopes);

  // Alert-card deep link: `/?case=SC-1042` (case param, NO conversation id)
  // resolves to the caller's existing case chat or starts one with the kickoff
  // auto-sent, then lands on /chat/:id?case=… — so the param survives as the
  // marker of what the chat is about, and a refresh there never re-sends
  // (the kickoff fires only on the id-less entry URL). Effect (not an event
  // handler): this syncs with the URL, an external system. The ref guards
  // StrictMode's double-invoke — one link click, one send.
  const [searchParams] = useSearchParams();
  const { conversationId: urlConversationId, shareId } = useParams<{
    conversationId: string;
    shareId: string;
  }>();
  const navigate = useNavigate();
  const caseParam = searchParams.get('case');
  const handledCaseRef = useRef(false);
  useEffect(() => {
    if (!caseParam || urlConversationId || !canChat || handledCaseRef.current) return;
    handledCaseRef.current = true;
    openCaseConversation(caseParam);
  }, [caseParam, urlConversationId, canChat]);
  const isAdmin = me.data ? hasAdminAccess(me.data.user) : false;
  const isEmpty = messages.length === 0 && !isTyping && !isLoadingMessages;

  // A saved conversation gets a header bar with Share: the dialog when it isn't
  // shared yet, Manage sharing once it is.
  const activeConversation = conversations.find((c) => c.id === activeId);
  const shareControls = useSharingLogic(activeConversation ? activeConversation.id : null);
  const [shareOpen, setShareOpen] = useState<'share' | 'manage' | null>(null);
  const openShare = () => setShareOpen(shareControls.share.data ? 'manage' : 'share');

  // An assistant turn's body: the tool-activity record ABOVE the bubble (live
  // rows while the turn runs, the settled pill once it's done — and the same
  // record on a replayed thread), then ONE bubble with the whole answer. The
  // activity card replaces the old inline markers and the ephemeral chip.
  // One writing-gap word per turn: re-picked when a new turn goes live, stable
  // across the renders within it (a word that flickers reads as glitching).
  // liveMessageId is deliberately the only dependency: the pick re-rolls per
  // turn, not per render.
  const writingLabel = useMemo(
    () => pickActivityLabel(TOOL_ACTIVITY_WRITING_LABELS),
    [liveMessageId],
  );

  // A link in an answer (a run page, a document) opens beside the chat,
  // never in place of it: navigating away loses the conversation the person
  // was in the middle of. Delegated so it covers every anchor the markdown
  // renders without reaching into the design-system component.
  const openLinksBesideChat = (event: MouseEvent<HTMLElement>) => {
    const anchor = (event.target as HTMLElement).closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    window.open(anchor.href, '_blank', 'noopener');
  };

  const renderAssistantBody = (m: ChatMessage) => {
    const isStreaming = m.id === streamingMessageId;
    // Defensive: a settled-but-empty turn (shape drift, an old ghost) renders
    // nothing rather than an avatar with a blank body.
    if (m.id !== liveMessageId && m.content.length === 0 && (m.toolActivity?.length ?? 0) === 0) {
      return null;
    }
    // Live for the WHOLE turn (tools + thinking + streaming), not just while
    // text reveals — collapsing to the pill mid-turn read as a stall.
    const isLive = m.id === liveMessageId;
    const activity = m.toolActivity ?? [];
    // The card is the turn's ONLY live container — no typing dots. Its shimmer
    // row covers every "alive but no tool running" gap: `true` (pooled word)
    // before anything happens, the writing word once tools are done and the
    // answer hasn't started. Off while a tool spins or text streams.
    const anyRunning = activity.some((t) => t.status === 'running');
    const thinking =
      isLive && !anyRunning && m.content.length === 0
        ? activity.length > 0
          ? writingLabel
          : true
        : false;
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        {(activity.length > 0 || thinking !== false) && (
          <Chat.ToolActivity items={activity} settled={!isLive} thinking={thinking} />
        )}
        {m.content.length > 0 && (
          <Chat.MessageBubble>
            {/* A message the bot sent on its own: what happened on top, why you got it below. */}
            {m.bot && (
              <Chat.MessageHeader>
                {/* Red only when the bot said it can't wait, so it keeps catching the eye. */}
                {m.bot.urgent ? (
                  <>
                    <Badge variant="danger">{m.bot.headline}</Badge>
                    <span>{formatDayTime(m.bot.sentAt)}</span>
                  </>
                ) : (
                  <>
                    <span className="text-[13px] font-semibold text-ink">{m.bot.headline}</span>
                    <span>· {formatDayTime(m.bot.sentAt)}</span>
                  </>
                )}
              </Chat.MessageHeader>
            )}
            <div onClickCapture={openLinksBesideChat}>
              <Chat.MessageMarkdown streaming={isStreaming}>{m.content}</Chat.MessageMarkdown>
            </div>
            {m.bot && (
              <Chat.MessageFooter>
                <span>
                  Why you&apos;re seeing this: {m.bot.reason}.
                  {m.bot.duty && <> From the {m.bot.duty} duty.</>}
                </span>
              </Chat.MessageFooter>
            )}
          </Chat.MessageBubble>
        )}
        {m.question && renderQuestion(m.question)}
      </div>
    );
  };

  // The bot's question: open takes a tap or typed answer; closed replays locked.
  const renderQuestion = (q: BotQuestion) => {
    // A card only for a pick among named choices; a yes or no is asked and answered in words.
    if (q.options.length < 2) return null;
    if (q.status === 'open' || q.status === 'answered') {
      return (
        <div className="flex flex-col gap-1">
          <Chat.Choices
            question={q.question}
            options={q.options}
            allowOther={q.allowOther}
            otherPlaceholder="Or type your own answer"
            submitLabel="Send answer"
            {...(q.answered ? { answered: q.answered } : { onSubmit: (a) => answerQuestion(q, a) })}
          />
          {q.answeredBy && (
            <p className="text-xs text-ink-subtle">
              {q.answeredBy} {q.answered?.kind === 'text' ? 'answered this' : 'picked this'}.
            </p>
          )}
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-1">
        <Chat.Choices
          question={q.question}
          options={q.options.map((o) => ({ ...o, disabled: true }))}
        />
        <p className="text-xs text-ink-subtle">
          {q.status === 'expired'
            ? 'No answer within a day, so the bot dropped this question.'
            : 'A newer question replaced this one.'}
        </p>
      </div>
    );
  };

  // A conversation row. Delete reveals on hover at lg+ and stays visible on
  // touch; it routes through a confirm modal because it's irreversible.
  const renderRow = (c: Conversation) => (
    <div key={c.id} className="group flex items-center gap-0.5">
      <button
        type="button"
        onClick={() => selectConversation(c.id)}
        className={`${sideNavItemClassName(!shareId && c.id === activeId)} min-w-0 flex-1 text-left`}
      >
        <span className="truncate">{c.title}</span>
      </button>
      <div className="flex shrink-0 items-center opacity-100 transition-opacity focus-within:opacity-100 lg:opacity-0 lg:group-hover:opacity-100">
        <button
          type="button"
          aria-label="Delete conversation"
          onClick={() => setConfirmDelete(c)}
          className="rounded p-1 text-ink-muted transition-colors hover:bg-surface-hover hover:text-danger"
        >
          <Trash2 size={15} aria-hidden />
        </button>
      </div>
    </div>
  );

  return (
    <DSAppShell>
      <PlatformHeader onMenuClick={() => setNavOpen(true)} />
      <div className="flex min-h-0 flex-1">
        {/* Conversation list — a static rail at lg+, a slide-in drawer below lg
            (opened by the header hamburger; closes on selection). */}
        <SideNav open={navOpen} onOpenChange={setNavOpen}>
          {myBots.bots.length > 0 && (
            <>
              <div className="flex flex-col gap-1.5">
                {myBots.bots.map((bot) => (
                  <BotPin
                    key={bot.id}
                    bot={bot}
                    active={!shareId && bot.threadId === activeId}
                    onOpen={() => {
                      setNavOpen(false);
                      void myBots.openBot(bot);
                    }}
                  />
                ))}
              </div>
              <SideNav.Divider />
            </>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="w-full"
            onClick={newConversation}
            disabled={!canChat}
          >
            New chat
          </Button>
          <SharedWithMeSection
            enabled={canChat}
            activeShareId={shareId}
            onOpen={(id) => {
              navigate(`/shared/${id}`);
              setNavOpen(false);
            }}
          />
          <div className="px-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">
            Recent
          </div>
          {conversations.map((c) => renderRow(c))}
        </SideNav>

        {/* Chat pane. min-w-0: without it this flex item can't shrink below
            its content's min-width, so one wide message (a markdown table)
            widens the whole page on phones instead of scrolling in place. */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {me.isLoading ? (
            <div className="flex flex-1 items-center justify-center text-sm text-ink-muted">
              Loading…
            </div>
          ) : canChat && shareId ? (
            <SharedConversationView key={shareId} shareId={shareId} />
          ) : canChat ? (
            <>
              {activeBot && (
                <div className="flex items-center gap-3 border-b border-outline px-5 py-2.5">
                  <BotAvatar size="sm" />
                  <span className="min-w-0 truncate text-sm font-semibold text-ink">
                    {activeBot.name}
                  </span>
                  <div className="flex-1" />
                  <Button variant="secondary" size="sm" onClick={() => setWatchingOpen(true)}>
                    <Eye size={14} aria-hidden className="mr-1.5 shrink-0" />
                    What I watch
                  </Button>
                  {!activeBot.turnedOff && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={myBots.isSwitching}
                      onClick={() => void myBots.setTurnedOff(activeBot.id, true)}
                    >
                      Turn off
                    </Button>
                  )}
                  <WhatIWatchPanel
                    open={watchingOpen}
                    onOpenChange={setWatchingOpen}
                    botId={activeBot.id}
                    botName={activeBot.name}
                  />
                </div>
              )}
              {activeConversation && !isEmpty && (
                <div className="flex items-center gap-3 border-b border-outline px-5 py-2.5">
                  <span className="min-w-0 truncate text-sm font-semibold text-ink">
                    {activeConversation.title}
                  </span>
                  <div className="flex-1" />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openShare}
                    className="gap-1.5"
                    disabled={shareControls.share.isLoading}
                  >
                    <Share2 size={14} aria-hidden />
                    Share
                  </Button>
                </div>
              )}
              <Chat aria-label="SC Assistant chat">
                {/* pin (DS 2.1.0) follows a streaming answer and LETS GO when the reader
                scrolls up — the old effect here fired on every streamed chunk and
                made scrolling up impossible (feedback bd2d2f75). Keyed per thread so each one
                opens at its newest message, not where the last thread was left. */}
                <Chat.MessageList key={activeId} ref={listRef} pin>
                  {earlier.canLoad && !isLoadingMessages && (
                    <div className="flex justify-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={earlier.loading}
                        onClick={() => void showEarlier()}
                      >
                        {earlier.loading ? 'Loading earlier messages…' : 'Show earlier messages'}
                      </Button>
                    </div>
                  )}
                  {earlier.capped && !isLoadingMessages && (
                    <p className="text-center text-xs text-ink-subtle">
                      Earlier messages aren’t shown here.
                    </p>
                  )}
                  {isLoadingMessages ? (
                    <div className="flex flex-1 items-center justify-center text-sm text-ink-muted">
                      Loading conversation…
                    </div>
                  ) : isEmpty && activeBot ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-ink-muted">
                      <BotAvatar size="lg" />
                      <p className="text-lg font-semibold text-ink">{activeBot.name}</p>
                      <p className="max-w-sm text-sm">
                        {activeBot.description ||
                          'Your thread with this bot. Ask it anything, and it keeps the conversation going over time.'}
                      </p>
                    </div>
                  ) : isEmpty ? (
                    <div className="flex flex-1 flex-col items-center justify-center text-center text-ink-muted">
                      <span className="mb-3 text-primary">
                        <SCLogo className="h-10 w-10" />
                      </span>
                      <p className="text-lg font-semibold text-ink">How can I help?</p>
                      <p className="mt-1 max-w-sm text-sm">
                        Ask about a unit, a work order, or fleet health.
                      </p>
                    </div>
                  ) : (
                    messages.map((m) => {
                      // Feedback shows on any FINISHED assistant turn — streamed this
                      // session OR replayed from history — because `feedbackId` (the
                      // message id) is present on both. Skip the turn still streaming.
                      const feedbackMessageId =
                        m.role === 'assistant' && m.id !== streamingMessageId
                          ? m.feedbackId
                          : undefined;
                      return (
                        <Chat.Message key={m.id} role={m.role}>
                          {m.role === 'assistant' &&
                            (activeBot ? (
                              <BotAvatar size="md" />
                            ) : (
                              <Chat.MessageAvatar>SC</Chat.MessageAvatar>
                            ))}
                          {m.role === 'assistant' ? (
                            <div className="flex min-w-0 flex-col gap-1.5">
                              {renderAssistantBody(m)}
                              {/* Wait for prior ratings before rendering, so an already-rated
                                turn opens in its "thanks" state rather than flashing clickable
                                buttons. Keyed by messageId so it remounts per turn (its
                                submitted state seeds once from submittedRating). */}
                              {feedbackMessageId && !isLoadingFeedback && (
                                <Chat.MessageFeedback
                                  key={feedbackMessageId}
                                  categories={FEEDBACK_CATEGORIES}
                                  submittedRating={feedbackByMessage[feedbackMessageId] ?? null}
                                  onSubmit={(fb) =>
                                    submitFeedback({
                                      messageId: feedbackMessageId,
                                      rating: fb.rating,
                                      comment: fb.comment,
                                      categories: fb.categories,
                                    })
                                  }
                                />
                              )}
                            </div>
                          ) : (
                            <Chat.MessageBubble>
                              <Chat.MessageContent>{m.content}</Chat.MessageContent>
                            </Chat.MessageBubble>
                          )}
                        </Chat.Message>
                      );
                    })
                  )}
                </Chat.MessageList>

                {activeBot && !activeBot.turnedOff && <PushAskBanner botName={activeBot.name} />}
                {activeBot?.turnedOff && (
                  <Banner variant="info" className="mx-4 mb-2">
                    <Banner.Body>
                      <Banner.Title>{activeBot.name} is off</Banner.Title>
                      <Banner.Description>
                        Your conversation stays here. Turn it back on to reply, and to hear from it
                        again.
                      </Banner.Description>
                    </Banner.Body>
                    <Banner.Actions>
                      <Button
                        size="sm"
                        disabled={myBots.isSwitching}
                        onClick={() => void myBots.setTurnedOff(activeBot.id, false)}
                      >
                        Turn on
                      </Button>
                    </Banner.Actions>
                  </Banner>
                )}
                <Chat.Composer
                  value={draft}
                  onChange={setDraft}
                  onSubmit={send}
                  disabled={isTyping || Boolean(activeBot?.turnedOff)}
                >
                  <Chat.ComposerInput
                    ref={inputRef}
                    placeholder={activeBot ? `Message ${activeBot.name}…` : 'Ask SC Assistant…'}
                  />
                  <div className="flex items-center justify-between">
                    <Chat.ComposerToolbar>
                      <span className="text-xs text-ink-subtle">SC Assistant</span>
                    </Chat.ComposerToolbar>
                    <Chat.ComposerSendButton />
                  </div>
                </Chat.Composer>
              </Chat>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-8">
              <div className="max-w-md text-center">
                <p className="mb-2 font-semibold text-ink">No assistant access yet.</p>
                <p className="text-sm text-ink-muted">
                  Your account doesn&apos;t have the <span className="font-mono">Agent.Run</span>{' '}
                  capability. Ask a tenant admin to grant it (directly or via a group).
                  {isAdmin && ' You can still manage the platform from Admin.'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmDelete && (
        <Modal open onOpenChange={() => setConfirmDelete(null)}>
          <Modal.Header>
            <Modal.Title>Delete conversation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-ink-muted">
              Delete &ldquo;{confirmDelete.title}&rdquo;? This permanently removes the conversation
              and its history — it can&apos;t be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                deleteConversation(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      <ShareDialog
        open={shareOpen === 'share'}
        onOpenChange={(open) => setShareOpen(open ? 'share' : null)}
        controls={shareControls}
      />
      <ManageSharingModal
        open={shareOpen === 'manage'}
        onOpenChange={(open) => setShareOpen(open ? 'manage' : null)}
        title={activeConversation?.title ?? ''}
        controls={shareControls}
      />
    </DSAppShell>
  );
};
